if (!customElements.get('tooto-store-locator')) {
  class TootoStoreLocator extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.visibleItems = [];
      this.regions = new Map();
      this.swiper = null;
      this.retryFrame = null;
      this.retryCount = 0;
      this.activeRegion = 'all';
      this.activeStoreId = null;
      this.pendingStoreId = null;
      this.isInitialized = false;
      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleFilterKeydown = this.handleFilterKeydown.bind(this);
      this.handleMarkerClick = this.handleMarkerClick.bind(this);
      this.handleComponentClick = this.handleComponentClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.slider = this.querySelector('[data-store-slider]');
      this.wrapper = this.querySelector('[data-store-wrapper]');
      this.stash = this.querySelector('[data-store-stash]');
      this.filterList = this.querySelector('[data-store-filter-list]');
      this.panel = this.querySelector('[data-store-panel]');
      this.markerLayer = this.querySelector('[data-store-markers]');
      this.status = this.querySelector('[data-store-status]');
      this.previousButton = this.querySelector('[data-store-previous]');
      this.nextButton = this.querySelector('[data-store-next]');

      if (!this.wrapper || !this.stash || !this.filterList || !this.panel) {
        this.classList.add('is-empty');
        return;
      }

      this.items = [
        ...this.wrapper.querySelectorAll(':scope > [data-store-item]'),
        ...this.stash.querySelectorAll(':scope > [data-store-item]'),
      ];

      if (!this.items.length) {
        this.classList.add('is-empty');
        return;
      }

      this.isInitialized = true;
      this.classList.remove('is-empty');
      this.collectRegions();
      this.buildFilters();
      this.buildMarkers();
      this.configureLinks();
      this.bindEvents();
      this.classList.add('is-ready');
      this.applyFilter('all', { announce: false });
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.destroySwiper();
      this.isInitialized = false;
    }

    collectRegions() {
      this.regions.clear();

      this.items.forEach((item) => {
        const key = item.dataset.regionKey;
        if (!key || this.regions.has(key)) return;

        this.regions.set(key, item.dataset.regionLabel || key);
      });
    }

    buildFilters() {
      this.filterList
        .querySelectorAll('[data-generated-region-filter]')
        .forEach((filter) => filter.remove());

      let index = 0;
      this.regions.forEach((label, key) => {
        const button = document.createElement('button');
        const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '-');
        button.id = `${this.id}-filter-${safeKey}-${index}`;
        button.className = 'tooto-store-locator__filter';
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('aria-controls', this.panel.id);
        button.tabIndex = -1;
        button.dataset.regionFilter = key;
        button.dataset.generatedRegionFilter = '';
        button.textContent = label;
        this.filterList.append(button);
        index += 1;
      });
    }

    buildMarkers() {
      if (!this.markerLayer) return;

      this.markerLayer.replaceChildren();

      this.items.forEach((item) => {
        if (item.dataset.showMarker !== 'true') return;

        const marker = document.createElement('button');
        marker.className = 'tooto-store-locator__marker';
        marker.type = 'button';
        marker.dataset.storeMarker = item.dataset.blockId;
        marker.dataset.regionKey = item.dataset.regionKey;
        marker.setAttribute('aria-label', item.dataset.markerLabel || item.dataset.storeTitle || '');
        marker.setAttribute('aria-pressed', 'false');
        marker.style.setProperty('--marker-x', `${Number(item.dataset.markerX) || 0}%`);
        marker.style.setProperty('--marker-y', `${Number(item.dataset.markerY) || 0}%`);
        marker.style.setProperty('--marker-x-mobile', `${Number(item.dataset.markerXMobile) || 0}%`);
        marker.style.setProperty('--marker-y-mobile', `${Number(item.dataset.markerYMobile) || 0}%`);

        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 32 40');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('focusable', 'false');

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M16 1.5C8.27 1.5 2 7.77 2 15.5 2 26 16 38.5 16 38.5S30 26 30 15.5C30 7.77 23.73 1.5 16 1.5Z');
        path.setAttribute('fill', 'currentColor');

        const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        dot.setAttribute('cx', '16');
        dot.setAttribute('cy', '15.5');
        dot.setAttribute('r', '5');
        dot.setAttribute('fill', 'rgb(var(--color-background))');

        icon.append(path, dot);
        marker.append(icon);
        this.markerLayer.append(marker);
      });
    }

    configureLinks() {
      const openNewTab = this.dataset.openLinksNewTab === 'true';

      this.items.forEach((item) => {
        const link = item.querySelector('[data-store-link]');
        const hint = item.querySelector('[data-store-new-window-hint]');
        if (!link) return;

        if (openNewTab) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          if (hint) hint.hidden = false;
        } else {
          link.removeAttribute('target');
          link.removeAttribute('rel');
          if (hint) hint.hidden = true;
        }
      });
    }

    bindEvents() {
      this.filterList.addEventListener('click', this.handleFilterClick);
      this.filterList.addEventListener('keydown', this.handleFilterKeydown);
      this.markerLayer?.addEventListener('click', this.handleMarkerClick);
      this.addEventListener('click', this.handleComponentClick);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    unbindEvents() {
      this.filterList?.removeEventListener('click', this.handleFilterClick);
      this.filterList?.removeEventListener('keydown', this.handleFilterKeydown);
      this.markerLayer?.removeEventListener('click', this.handleMarkerClick);
      this.removeEventListener('click', this.handleComponentClick);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    handleFilterClick(event) {
      const filter = event.target.closest('[data-region-filter]');
      if (!filter || !this.filterList.contains(filter)) return;

      this.applyFilter(filter.dataset.regionFilter, {
        announce: true,
        scrollFilter: true,
      });
    }

    handleFilterKeydown(event) {
      const currentFilter = event.target.closest('[data-region-filter]');
      if (!currentFilter) return;

      const filters = Array.from(this.filterList.querySelectorAll('[data-region-filter]'));
      const currentIndex = filters.indexOf(currentFilter);
      if (currentIndex < 0) return;

      const isRtl = document.documentElement.dir === 'rtl';
      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = isRtl ? currentIndex - 1 : currentIndex + 1;
          break;
        case 'ArrowLeft':
          nextIndex = isRtl ? currentIndex + 1 : currentIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = filters.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      nextIndex = (nextIndex + filters.length) % filters.length;
      const nextFilter = filters[nextIndex];
      nextFilter.focus({ preventScroll: true });
      this.applyFilter(nextFilter.dataset.regionFilter, {
        announce: true,
        scrollFilter: true,
      });
    }

    handleMarkerClick(event) {
      const marker = event.target.closest('[data-store-marker]');
      if (!marker || !this.markerLayer.contains(marker)) return;

      this.applyFilter(marker.dataset.regionKey, {
        targetBlockId: marker.dataset.storeMarker,
        announce: true,
        scrollFilter: true,
      });
    }

    handleComponentClick(event) {
      if (this.dataset.designMode !== 'true') return;

      const link = event.target.closest('[data-store-link]');
      if (link && this.contains(link)) event.preventDefault();
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      const selectedItem = this.items.find((item) => item.dataset.blockId === blockId);
      if (!selectedItem) return;

      this.applyFilter(selectedItem.dataset.regionKey, {
        targetBlockId: selectedItem.dataset.blockId,
        announce: false,
        scrollFilter: true,
      });
    }

    handleSectionUnload(event) {
      if (event.target instanceof Node && event.target.contains(this)) this.destroySwiper();
    }

    applyFilter(regionKey, options = {}) {
      if (!this.items.length) return;

      let nextRegion = regionKey || 'all';
      let visibleItems =
        nextRegion === 'all'
          ? [...this.items]
          : this.items.filter((item) => item.dataset.regionKey === nextRegion);

      if (!visibleItems.length) {
        nextRegion = 'all';
        visibleItems = [...this.items];
      }

      this.activeRegion = nextRegion;
      this.visibleItems = visibleItems;
      this.classList.toggle('is-filtered', nextRegion !== 'all');
      this.destroySwiper();

      const visibleSet = new Set(visibleItems);
      this.items.forEach((item) => {
        if (visibleSet.has(item)) {
          this.wrapper.append(item);
        } else {
          this.stash.append(item);
        }
      });

      const activeFilter = this.updateFilterState(nextRegion);
      this.updateMarkerRegionState(nextRegion);

      let targetItem = visibleItems.find((item) => item.dataset.blockId === options.targetBlockId);
      if (!targetItem) targetItem = visibleItems[0];

      this.setActiveStore(targetItem?.dataset.blockId);
      this.initializeSwiper(targetItem?.dataset.blockId);

      if (activeFilter && options.scrollFilter) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        activeFilter.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }

      if (options.announce) {
        this.announceResults(visibleItems.length, nextRegion);
      } else if (this.status) {
        this.status.textContent = '';
      }
    }

    updateFilterState(regionKey) {
      let activeFilter = null;

      this.filterList.querySelectorAll('[data-region-filter]').forEach((filter) => {
        const isActive = filter.dataset.regionFilter === regionKey;
        filter.classList.toggle('is-active', isActive);
        filter.setAttribute('aria-selected', String(isActive));
        filter.tabIndex = isActive ? 0 : -1;
        if (isActive) activeFilter = filter;
      });

      if (activeFilter) {
        this.panel.setAttribute('aria-labelledby', activeFilter.id);
      }

      return activeFilter;
    }

    updateMarkerRegionState(regionKey) {
      this.markerLayer?.querySelectorAll('[data-store-marker]').forEach((marker) => {
        marker.classList.toggle(
          'is-region-match',
          regionKey !== 'all' && marker.dataset.regionKey === regionKey
        );
      });
    }

    setActiveStore(blockId) {
      if (!blockId) return;

      this.activeStoreId = blockId;
      this.items.forEach((item) => {
        item.classList.toggle('is-current', item.dataset.blockId === blockId);
      });
      this.markerLayer?.querySelectorAll('[data-store-marker]').forEach((marker) => {
        const isCurrent = marker.dataset.storeMarker === blockId;
        marker.classList.toggle('is-current', isCurrent);
        marker.setAttribute('aria-pressed', String(isCurrent));
      });
    }

    initializeSwiper(targetBlockId) {
      if (!this.slider || !this.visibleItems.length) return;

      this.pendingStoreId = targetBlockId;

      if (typeof window.Swiper !== 'function') {
        if (this.retryCount < 180) {
          this.retryCount += 1;
          this.retryFrame = requestAnimationFrame(() => this.initializeSwiper(this.pendingStoreId));
        }
        return;
      }

      this.retryCount = 0;
      this.retryFrame = null;

      const slides = Array.from(this.wrapper.querySelectorAll(':scope > [data-store-item]'));
      const initialSlide = Math.max(
        0,
        slides.findIndex((slide) => slide.dataset.blockId === targetBlockId)
      );
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const columnsDesktop = Number(this.dataset.columnsDesktop) || 4;
      const cardGap = Number(this.dataset.cardGap) || 24;
      const config = {
        loop: false,
        rewind: false,
        initialSlide,
        slidesPerView: 'auto',
          spaceBetween: 12,
        slidesPerGroup: 1,
        speed: reducedMotion ? 0 : 800,
        centeredSlides: true,
        centeredSlidesBounds: true,
        grabCursor: slides.length > 1,
        watchOverflow: true,
        roundLengths: true,
        mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 1,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.previousLabel,
          nextSlideMessage: this.dataset.nextLabel,
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            centeredSlides: false,
              spaceBetween: cardGap,
          },
          990: {
            slidesPerView: 4,
            centeredSlides: false,
              spaceBetween: cardGap,
          },
        },
        on: {
          init: (swiper) => this.handleSlideChange(swiper),
          slideChange: (swiper) => this.handleSlideChange(swiper),
        },
      };

      if (this.previousButton && this.nextButton) {
        config.navigation = {
          prevEl: this.previousButton,
          nextEl: this.nextButton,
        };
      }

      this.swiper = new window.Swiper(this.slider, config);
      this.classList.add('has-swiper');
      this.setActiveStore(targetBlockId || slides[0]?.dataset.blockId);
    }

    handleSlideChange(swiper) {
      const activeItem = this.wrapper.children[swiper.activeIndex];
      if (activeItem?.dataset.blockId) this.setActiveStore(activeItem.dataset.blockId);
    }

    announceResults(count, regionKey) {
      if (!this.status) return;

      const allFilter = this.filterList.querySelector('[data-region-filter="all"]');
      const regionLabel =
        regionKey === 'all' ? allFilter?.textContent?.trim() : this.regions.get(regionKey) || regionKey;
      this.status.textContent = `${regionLabel}: ${count}`;
    }

    destroySwiper() {
      if (this.retryFrame) {
        cancelAnimationFrame(this.retryFrame);
        this.retryFrame = null;
      }

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }

      this.swiper = null;
      this.retryCount = 0;
      this.classList.remove('has-swiper');
    }
  }

  customElements.define('tooto-store-locator', TootoStoreLocator);
}
